import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { hasQuantity } from "../../../lib/quantityInput";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { PositionPicker } from "../../../components/mobile/PositionPicker";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { FlowPhotoSlot } from "../../../components/mobile/FlowPhotoSlot";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

const REASONS = [
    { v: "lost", l: "Verloren" },
    { v: "found", l: "Gefunden" },
    { v: "damage", l: "Beschädigt" },
    { v: "count_difference", l: "Zähl-Differenz" },
    { v: "unknown", l: "Unbekannt" },
];

export default function KorrekturFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [positions, setPositions] = useState([]);
    const [articles, setArticles] = useState({});
    const [compartments, setCompartments] = useState({});
    const [positionId, setPositionId] = useState("");
    const [scanMatches, setScanMatches] = useState(null);
    const [target, setTarget] = useState(0);
    const [reason, setReason] = useState("count_difference");
    const [comment, setComment] = useState("");
    const [photoId, setPhotoId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmZero, setConfirmZero] = useState(false);

    const currentPosition = positions.find((p) => p.id === positionId) || null;

    // #135 Befund 8: Zielmenge nie stumm auf 0 lassen -- bei Positionswahl mit
    // dem Ist-Bestand vorbelegen, damit "0 buchen" eine bewusste Handlung ist.
    const selectPosition = useCallback((id) => {
        setPositionId(id);
        setScanMatches(null);
        const pos = positions.find((p) => p.id === id);
        if (pos) setTarget(pos.quantity ?? 0);
    }, [positions]);

    useEffect(() => {
        Promise.all([
            rpc.get("/master/stock-positions"),
            rpc.get("/master/articles"),
            rpc.get("/master/compartments"),
        ]).then(
            ([p, a, c]) => {
                if (p.ok) setPositions(p.data);
                if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
                if (c.ok) setCompartments(Object.fromEntries(c.data.map((x) => [x.id, x])));
            }
        );
    }, []);

    // Scan-Hub bietet "Korrektur" mit Artikel-Prefill an -- Position vorbelegen.
    useEffect(() => {
        if (!prefill?.articleId || positions.length === 0) return;
        const matches = positions.filter((p) => p.article_id === prefill.articleId);
        if (matches.length === 1) {
            selectPosition(matches[0].id);
            toast.success("Position aus Scan übernommen.");
        } else if (matches.length > 1) {
            setScanMatches(matches.map((position) => position.id));
            toast.warning(`${matches.length} Positionen gefunden -- bitte auswählen.`);
        }
    }, [prefill, positions, selectPosition]);

    const submit = async () => {
        if (!positionId) return toast.error("Position wählen.");
        if (!comment.trim()) return toast.error("Kommentar ist Pflicht bei Korrektur.");
        // #209: leeres Feld ist keine 0 -- nie still auf 0 korrigieren.
        if (!hasQuantity(target, { min: 0 })) return toast.error("Zielmenge eingeben.");
        // #135 Befund 8: Handbuch 5.2 warnt vor versehentlichem "0 buchen".
        if (target === 0 && (currentPosition?.quantity ?? 0) !== 0) {
            setConfirmZero(true);
            return;
        }
        await book();
    };

    const book = async () => {
        setSubmitting(true);
        const res = await enqueueRpc({
            path: "/movements",
            rpcName: "Korrektur",
            summary: `Neu: ${target} (${reason})`,
            payload: {
                client_operation_id: flow.clientOperationId,
                type: "adjustment",
                from_position_id: positionId,
                quantity: target,
                reason,
                comment,
                evidence_photo_id: photoId || undefined,
            },
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) toast.warning("Offline -- Korrektur wartet in der Queue.");
            else return toast.error(res.error);
        } else {
            toast.success("Korrektur gebucht.");
        }
        flow.rotate();
        setPositionId("");
        setScanMatches(null);
        // absolute Zielmenge darf nie in die nächste Korrektur "durchsickern"
        setTarget(0);
        setComment("");
        setPhotoId(null);
    };

    return (
        <FlowShell
            testId="flow-korrektur"
            title="Schnell-Korrektur"
            kind="Schnellflow"
            subtitle="Absolute Menge, Grund + Kommentar Pflicht"
            onSubmit={submit}
            submitDisabled={!positionId || !comment.trim() || !hasQuantity(target, { min: 0 })}
            submitting={submitting}
        >
            <ScanBox
                testId="korr-scan"
                placeholder="Artikel oder Fach scannen"
                onResolved={(raw, resolved) => {
                    const matches = resolved?.kind === "article"
                        ? positions.filter((position) => position.article_id === resolved.id)
                        : resolved?.kind === "compartment"
                            ? positions.filter((position) => position.compartment_id === resolved.id)
                            : [];
                    if (matches.length === 1) {
                        selectPosition(matches[0].id);
                        toast.success("Position aus Scan übernommen.");
                    } else if (matches.length > 1) {
                        setPositionId("");
                        setScanMatches(matches.map((position) => position.id));
                        toast.warning(`${matches.length} Positionen gefunden -- direkt auswählen.`);
                    } else {
                        toast.warning(`Kein Bestand zu Code ${raw}.`);
                    }
                }}
            />
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Position</Label>
                {scanMatches ? (
                    <p className="text-xs text-primary" data-testid="korr-scan-matches">
                        {scanMatches.length} Scan-Treffer – Position wählen
                    </p>
                ) : null}
                <PositionPicker
                    value={positionId}
                    onChange={selectPosition}
                    positions={scanMatches ? positions.filter((p) => scanMatches.includes(p.id)) : positions}
                    articles={articles}
                    compartments={compartments}
                    testId="korr-position"
                    placeholder="Position wählen"
                />
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Neue Menge (absolut)
                    </Label>
                    {currentPosition ? (
                        <span className="text-xs text-muted-foreground" data-testid="korr-ist">
                            Ist-Bestand:{" "}
                            <span className="font-semibold text-foreground">
                                {currentPosition.quantity ?? 0}
                            </span>
                        </span>
                    ) : null}
                </div>
                <QuantityStepper value={target} onChange={setTarget} min={0} testId="korr-qty" />
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Grund</Label>
                <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger className="h-12" data-testid="korr-reason">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {REASONS.map((r) => (
                            <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kommentar</Label>
                <Input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="h-12"
                    aria-label="Kommentar"
                    data-testid="korr-comment"
                />
            </div>
            <FlowPhotoSlot
                sessionKey={flow.clientOperationId}
                label="Beleg / Schaden"
                entityType="movement"
                purpose="damage"
                value={photoId}
                onChange={setPhotoId}
                helpText="Optional -- Foto wird an die Buchung geknüpft."
                testId="korr-photo"
            />
            <AlertDialog open={confirmZero} onOpenChange={setConfirmZero}>
                <AlertDialogContent data-testid="korr-zero-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bestand auf 0 setzen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Die Korrektur bucht die absolute Menge. Ist-Bestand{" "}
                            {currentPosition?.quantity ?? 0} wird auf 0 gesetzt. Gezählten
                            Gesamtbestand eintragen, nicht die Differenz.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="korr-zero-cancel">
                            Abbrechen
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="korr-zero-confirm-submit"
                            onClick={() => {
                                setConfirmZero(false);
                                book();
                            }}
                        >
                            0 buchen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </FlowShell>
    );
}
